import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { PageHeader, StatusBadge, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import FilesPanel from "@/components/FilesPanel";
import { toast } from "sonner";
import { ArrowLeft, EnvelopeSimple, Phone, MapPin, Briefcase, FileText, CurrencyDollar, CheckCircle, WarningCircle, PaperPlaneTilt } from "@phosphor-icons/react";

export default function InterpreterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: intp, isLoading } = useQuery({ queryKey: ["interpreter", id], queryFn: async () => (await api.get(`/interpreters/${id}`)).data });
  const { data: allProjects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const projects = allProjects.filter((p) => p.interpreter_id === id);

  const completed = projects.filter((p) => p.status === "completed");
  const upcoming = projects.filter((p) => ["pending", "confirmed", "in_progress"].includes(p.status));
  const totalEarned = completed.reduce((s, p) => s + (p.interpreter_rate || 0) * (p.duration_hours || 1), 0);
  const totalHours = completed.reduce((s, p) => s + (p.duration_hours || 0), 0);

  const viewW9 = async () => {
    const r = await fetch(`${API}/interpreters/${id}/w9`, { credentials: "include" });
    if (!r.ok) return;
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  if (isLoading) return <div className="p-10 text-slate-400">Loading…</div>;
  if (!intp) return <div className="p-10 text-slate-400">Interpreter not found.</div>;

  return (
    <div data-testid="interpreter-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/interpreters")} className="mb-4" data-testid="back-to-interpreters">
        <ArrowLeft size={14} className="mr-1.5" /> Back to interpreters
      </Button>

      <PageHeader
        eyebrow="1099 Contractor"
        title={intp.full_name}
        description={intp.language_pairs?.join(" · ") || "Interpreter profile"}
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid="intp-detail-send-signing-link"
              onClick={async () => {
                try {
                  const resp = await api.post(`/interpreters/${id}/send-signing-link`);
                  toast.success(`Signing link sent to ${intp.email || "interpreter"}`);
                  if (resp.data?.sign_url) navigator.clipboard?.writeText(resp.data.sign_url)?.catch(() => {});
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to send signing link");
                }
              }}
            >
              <PaperPlaneTilt size={14} className="mr-1.5" /> Send e-sign link
            </Button>
            <StatusBadge status={intp.availability} />
          </div>
        }
      />

      <div className="card-flat p-5 mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <Info icon={<EnvelopeSimple size={14} />} label="Email" value={intp.email} />
        <Info icon={<Phone size={14} />} label="Phone" value={intp.phone} />
        <Info icon={<MapPin size={14} />} label="Location" value={intp.location || intp.city_state_zip} />
        <Info
          icon={<MapPin size={14} />}
          label="Travel radius"
          value={intp.max_travel_miles != null ? `${intp.max_travel_miles} mi` : "Remote only"}
        />
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">W-9</div>
          {intp.w9_path ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle size={12} weight="fill" /> On file
              </span>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={viewW9}>View</Button>
            </div>
          ) : (
            <span className="badge-pill bg-amber-50 text-amber-700 border-amber-200 mt-1 inline-flex">
              <WarningCircle size={12} weight="fill" /> Missing
            </span>
          )}
        </div>
      </div>

      {/* Rate row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Rate label="In-person" value={intp.hourly_rate} unit="/hr" />
        <Rate label="OPI phone" value={intp.opi_rate} unit="/hr" />
        <Rate label="VRI video" value={intp.vri_rate} unit="/hr" />
        <Rate label="Translation" value={intp.translation_rate} unit="/word" precision={4} />
      </div>

      {/* Notes: applicant intake + internal ops notes */}
      {(intp.application_notes || intp.notes) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="interpreter-notes">
          {intp.application_notes && (
            <div className="card-flat p-4" data-testid="application-notes-card">
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#B91C1C] font-semibold mb-2">From the application</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">"{intp.application_notes}"</p>
              <div className="text-[11px] text-slate-400 mt-3">Submitted on their public application form</div>
            </div>
          )}
          {intp.notes && intp.notes !== intp.application_notes && (
            <div className="card-flat p-4" data-testid="internal-notes-card">
              <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Internal notes</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{intp.notes}</p>
              <div className="text-[11px] text-slate-400 mt-3">Editable by ops from the interpreter edit form</div>
            </div>
          )}
        </div>
      )}

      <Tabs defaultValue="projects" data-testid="interpreter-tabs">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="projects"><Briefcase size={14} className="mr-1.5" /> Projects · {projects.length}</TabsTrigger>
          <TabsTrigger value="payouts"><CurrencyDollar size={14} className="mr-1.5" /> Payouts</TabsTrigger>
          <TabsTrigger value="files"><FileText size={14} className="mr-1.5" /> Files</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-4">
          {projects.length === 0 ? <Empty text="No projects assigned yet." /> : (
            <>
              {upcoming.length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Upcoming · {upcoming.length}</div>
                  <ProjectTable rows={upcoming} />
                </>
              )}
              {completed.length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mt-6 mb-2">Completed · {completed.length}</div>
                  <ProjectTable rows={completed} />
                </>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="payouts" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <KPI label="Total earned (all-time)" value={fmtMoney(totalEarned)} accent="emerald" />
            <KPI label="Total hours" value={totalHours.toFixed(1)} />
            <KPI label="Completed projects" value={completed.length} />
          </div>
          {totalEarned >= 600 && (
            <div className="card-flat p-4 bg-amber-50 border-amber-200 text-amber-900 mb-4">
              <b>1099-NEC required.</b> This contractor has earned ${totalEarned.toFixed(2)} — download the full 1099 report from <Link to="/reports" className="underline">Reports</Link>.
            </div>
          )}
          {completed.length > 0 && <ProjectTable rows={completed} showEarned />}
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <div className="card-flat p-5">
            <FilesPanel entityType="interpreter" entityId={id} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProjectTable({ rows, showEarned }) {
  return (
    <div className="card-flat overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-5 py-2">Project</th>
            <th className="text-left px-3 py-2">Scheduled</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-5 py-2">{showEarned ? "Earned" : "Rate"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.project_id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-5 py-2">
                <div className="font-medium text-slate-900">{p.title}</div>
                <div className="text-xs text-slate-500">{p.project_type} · {p.language} · {p.duration_hours}h</div>
              </td>
              <td className="px-3 py-2 text-slate-600 text-xs">{fmtDate(p.scheduled_at)}</td>
              <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
              <td className="px-5 py-2 text-right font-mono">
                {showEarned ? fmtMoney((p.interpreter_rate || 0) * (p.duration_hours || 1)) : fmtMoney(p.interpreter_rate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Info({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{icon}{label}</div>
      <div className="text-sm text-slate-900 mt-1">{value || "—"}</div>
    </div>
  );
}

function Rate({ label, value, unit, precision = 2 }) {
  return (
    <div className="card-flat p-4">
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-xl font-bold text-slate-900 mt-1">
        ${(value || 0).toFixed(precision)}<span className="text-xs text-slate-400 font-normal ml-1">{unit}</span>
      </div>
    </div>
  );
}

function KPI({ label, value, accent = "slate" }) {
  const tones = { emerald: "text-emerald-700", slate: "text-slate-900" };
  return (
    <div className="card-flat p-4">
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${tones[accent]}`}>{value}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="card-flat p-8 text-center text-sm text-slate-400">{text}</div>;
}
